import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { captureException } from "@/lib/error-logger";
import { BrowserRouter, HashRouter, Routes, Route } from "react-router-dom";

// Desktop (Electron) serves the app from file://, where path-based routing
// cannot resolve deep links — fall back to hash routing there.
const Router = typeof window !== "undefined" && window.location.protocol === "file:" ? HashRouter : BrowserRouter;
import { ThemeProvider } from "next-themes";
import { toast } from "sonner";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { ErrorBoundary } from "./components/ErrorBoundary";
const Auth = lazy(() => import("./pages/Auth"));

// Franchise Shell (combines ProtectedRoute + Layout + Outlet)
const FranchiseShell = lazy(() => import("./components/franchise/FranchiseShell").then(m => ({ default: m.FranchiseShell })));

// Lazy-loaded Franchise Pages
const HQDashboard = lazy(() => import("./pages/franchise/HQDashboard"));
const HQCenterReport = lazy(() => import("./pages/franchise/HQCenterReport"));
const SalesReports = lazy(() => import("./pages/franchise/SalesReports"));
const PerformanceDashboard = lazy(() => import("./pages/franchise/PerformanceDashboard"));
const Brands = lazy(() => import("./pages/franchise/Brands"));
const Branches = lazy(() => import("./pages/franchise/Branches"));
const HQOrders = lazy(() => import("./pages/franchise/HQOrders"));
const Users = lazy(() => import("./pages/franchise/Users"));
const AccountantDashboard = lazy(() => import("./pages/franchise/AccountantDashboard"));
const DispatcherDashboard = lazy(() => import("./pages/franchise/DispatcherDashboard"));
const ShipmentHistory = lazy(() => import("./pages/franchise/ShipmentHistory"));
const MysteryShopperDashboard = lazy(() => import("./pages/franchise/MysteryShopperDashboard"));
const AdvancedReports = lazy(() => import("./pages/franchise/AdvancedReports"));
const BusinessAnalytics = lazy(() => import("./pages/franchise/BusinessAnalytics"));
const MenuManagement = lazy(() => import("./pages/franchise/MenuManagement"));
const PriceGroups = lazy(() => import("./pages/franchise/PriceGroups"));
const Warehouse = lazy(() => import("./pages/franchise/Warehouse"));
const ActivityLogs = lazy(() => import("./pages/franchise/ActivityLogs"));
const UnitManagement = lazy(() => import("./pages/franchise/UnitManagement"));
const RecipeManagement = lazy(() => import("./pages/franchise/RecipeManagement"));
const Applications = lazy(() => import("./pages/franchise/Applications"));
const Notifications = lazy(() => import("./pages/franchise/Notifications"));
const CurrentAccountReportCard = lazy(() => import("./pages/franchise/CurrentAccountReportCard"));
const CurrentAccountStatement = lazy(() => import("./pages/franchise/CurrentAccountStatement"));
const CurrentAccountInvoices = lazy(() => import("./pages/franchise/CurrentAccountInvoices"));
const CurrentAccountOrders = lazy(() => import("./pages/franchise/CurrentAccountOrders"));
const CurrentAccountPayments = lazy(() => import("./pages/franchise/CurrentAccountPayments"));
const CurrentAccountReturnRequests = lazy(() => import("./pages/franchise/CurrentAccountReturnRequests"));

const HQFinance = lazy(() => import("./pages/franchise/HQFinance"));
const BranchMonitor = lazy(() => import("./pages/franchise/BranchMonitor"));

// NOTE: onError handlers are observability-only — they log to `error_logs`
// and do NOT change retry / throwOnError behaviour or how pages render errors.
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      captureException(error, { scope: `query:${String(query.queryKey?.[0] ?? 'unknown')}` });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      captureException(error, { scope: `mutation:${String(mutation.options.mutationKey?.[0] ?? 'unknown')}` });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
    },
  },
});

// Suspense fallback for lazy-loaded routes. Renders during app bootstrap,
// before LanguageProvider mounts, so uses the canonical TR dictionary.
import { tr as trDict } from "@/i18n";
const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background px-4">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      <span className="text-sm text-muted-foreground">{trDict.appShell.loading}</span>
    </div>
  </div>
);

// Global unhandled rejection handler component
function GlobalErrorHandler() {
  useEffect(() => {
    const handler = (event: PromiseRejectionEvent) => {
      console.error("Unhandled promise rejection:", event.reason);
      toast.error(trDict.appShell.globalError);
      event.preventDefault();
    };
    
    window.addEventListener("unhandledrejection", handler);
    return () => window.removeEventListener("unhandledrejection", handler);
  }, []);
  
  return null;
}

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <GlobalErrorHandler />
        <Toaster />
        <Sonner />
        <Router>
          <ErrorBoundary scope="root">
            <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              
              {/* Franchise Routes - Nested under single shell so layout persists */}
              <Route path="/franchise" element={<FranchiseShell />}>
                {/* HQ Routes */}
                <Route path="hq" element={<HQDashboard />} />
                <Route path="hq/brands" element={<Brands />} />
                <Route path="hq/branches" element={<Branches />} />
                <Route path="hq/menu" element={<MenuManagement />} />
                
                <Route path="hq/orders" element={<HQOrders />} />
                
                <Route path="hq/price-groups" element={<PriceGroups />} />
                <Route path="hq/recipes" element={<RecipeManagement />} />
                <Route path="hq/reports" element={<SalesReports />} />
                <Route path="hq/center-report" element={<HQCenterReport />} />
                <Route path="hq/reports/advanced" element={<AdvancedReports />} />
                <Route path="hq/reports/business" element={<BusinessAnalytics />} />
                <Route path="hq/performance" element={<PerformanceDashboard />} />
                <Route path="hq/users" element={<Users />} />

                {/* Call Center Routes */}
                <Route path="hq/applications" element={<Applications />} />
                <Route path="hq/notifications" element={<Notifications />} />
                
                {/* Warehouse */}
                <Route path="hq/warehouse" element={<Warehouse />} />
                <Route path="hq/unit-management" element={<UnitManagement />} />
                <Route path="hq/shipment-history" element={<ShipmentHistory />} />
                <Route path="hq/activity-logs" element={<ActivityLogs />} />
                <Route path="hq/finance" element={<HQFinance />} />
                <Route path="hq/branch-monitor" element={<BranchMonitor />} />

                {/* Current Account Routes */}
                <Route path="hq/current-accounts/report-card" element={<CurrentAccountReportCard />} />
                <Route path="hq/current-accounts/statement" element={<CurrentAccountStatement />} />
                <Route path="hq/current-accounts/invoices" element={<CurrentAccountInvoices />} />
                <Route path="hq/current-accounts/orders" element={<CurrentAccountOrders />} />
                <Route path="hq/current-accounts/payments" element={<CurrentAccountPayments />} />
                <Route path="hq/current-accounts/return-requests" element={<CurrentAccountReturnRequests />} />
                
                
                {/* Employee Routes */}
                <Route path="employees/accountant" element={<AccountantDashboard />} />
                <Route path="employees/dispatcher" element={<DispatcherDashboard />} />
                <Route path="employees/mystery" element={<MysteryShopperDashboard />} />
              </Route>
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </ErrorBoundary>
        </Router>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
